<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { watch, nextTick } from 'vue';

type Article = {
  Id: string;
  Title: string;
  KnowledgeArticleId: string;
};

type FullArticle = {
  title: string;
  details: string;
};

const route = useRoute();
const router = useRouter();

// Single chained async call: get article -> search related articles
const {
  data: articleData,
  pending: loading,
  error,
} = useFetch<{ fullArticle: FullArticle; relatedArticles: Article[] }>(
  () => `/api/sales-force-articles/get-article/${route.params.id}`,
  {
    transform: async (data: any) => {
      // Transform the main article
      const layoutItems = data.layoutItems ?? [];
      const titleItem = layoutItems.find((item: any) => item.name === 'Title');
      const detailsItem = layoutItems.find((item: any) => item.name === 'General_Details');

      const fullArticle: FullArticle = {
        title: titleItem?.value ?? '',
        details: detailsItem?.value ?? '',
      };

      // Chain the search call here to get related articles
      let relatedArticles: Article[] = [];

      if (fullArticle.title) {
        try {
          const searchResponse = await $fetch<any>('/api/sales-force-articles/search-articles/', {
            method: 'POST',
            body: { search_query: fullArticle.title },
          });
          const allResults = searchResponse.searchRecords ?? [];
          console.log('Search response:', searchResponse);
          console.log('All results:', allResults);
          relatedArticles = allResults.filter((article: Article) => article.KnowledgeArticleId !== route.params.id);
          console.log('Filtered related articles:', relatedArticles);
        } catch (e) {
          console.error('Failed to fetch related articles:', e);
          // Continue gracefully if related articles fail
        }
      }

      return { fullArticle, relatedArticles };
    },
    watch: [() => route.params.id],
  },
);

const scrollToHash = (hash: string) => {
  const el = document.getElementById(hash.slice(1));
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const handleContentClick = (event: MouseEvent) => {
  const anchor = (event.target as HTMLElement).closest('a');
  if (!anchor) return;

  const href = anchor.getAttribute('href');
  if (!href?.startsWith('#')) return;

  event.preventDefault();
  scrollToHash(href);
};

// Add this watch right after your useFetch or near the bottom of script setup
watch(
  loading,
  (isNowLoading) => {
    // We only care when loading FINISHES (isNowLoading === false)
    // and when there is actually a hash in the URL
    if (!isNowLoading && route.hash) {
      nextTick(() => {
        // Remove the '#' from the start of the string
        const elementId = route.hash.slice(1);
        const element = document.getElementById(elementId);

        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
    }
  },
  { immediate: true },
);

const fullArticle = computed(() => articleData.value?.fullArticle);
const relatedArticles = computed(() => articleData.value?.relatedArticles ?? []);
const hasRelatedArticles = computed(() => {
  console.log('articleData:', articleData.value);
  console.log('relatedArticles computed:', relatedArticles.value);
  console.log('hasRelatedArticles check:', relatedArticles.value.length > 0);
  return relatedArticles.value.length > 0;
});

const handleSearch = async (searchQuery: string) => {
  await router.push({
    path: '/student-experience/academic-services/bruin-support/bruin-support-articles/results',
    query: { q: searchQuery },
  });
};

useHead({
  title: () => fullArticle.value?.title || 'Bruin Support Article',
});

useSeoMeta({
  ogTitle: () => fullArticle.value?.title || 'Bruin Support Article',
  ogDescription: () => fullArticle.value?.details?.replace(/<[^>]*>?/gm, '').slice(0, 160) ?? '',
});
</script>

<template>
  <section class="bg-accent-25">
    <LayoutsContainer>
      <BruinSearchForm @search="handleSearch" />

      <div v-if="loading" class="text-center">
        <p>Loading article...</p>
      </div>
      <div v-else-if="error" class="text-center text-red-600"><p>Failed to load article.</p></div>

      <div v-else>
        <div class="grid grid-cols-1 gap-8 bg-white p-6 md:grid-cols-10">
          <div :class="hasRelatedArticles ? 'md:col-span-6 lg:col-span-7' : 'col-span-10'" class="col-span-1">
            <a
              :href="
                '/student-experience/academic-services/bruin-support/bruin-support-articles/' + route.params.id + '/'
              "
            >
              <TypographyTitle as="h1" size="lg" class="mb-4">
                {{ fullArticle?.title }}
              </TypographyTitle>
            </a>
            <div
              class="cms-content bruin-support-article mb-10"
              @click="handleContentClick"
              v-html="fullArticle?.details"
            ></div>
          </div>
          <div v-if="hasRelatedArticles" class="col-span-1 md:col-span-4 lg:col-span-3">
            <BruinRelatedArticles :search-results="relatedArticles" :current-article-id="route.params.id as string" />
          </div>
        </div>
      </div>
    </LayoutsContainer>
  </section>
</template>
